import React, { useEffect, useState } from 'react';
import { Button, Table } from 'ui';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { config } from 'config/config';
import { Checkbox, Divider, message, Popover } from 'antd';
import generalHelpers from 'utils/generalHelpers';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import coursesApiClient from '../../../utils/coursesApiClient';
import FileDownload from 'js-file-download';
import { AbsenceTag } from 'components/Tags';

const defaultFields: string[] = [
    'date',
    'start',
    'end',
    'sessionLength',
    'status',
    'sessionTitle',
    'fullName',
    'position',
    'organizationalUnit',
    'department',
    'organization',
    'email',
    'absence',
];

const cacheKey = 'sessionColumns_SESSION';

// Saved column selection; missing or empty means the user never saved one,
// so every column is shown by default.
const readCachedColumns = (): string[] | null => {
    const cached = localStorage.getItem(cacheKey);

    return cached ? cached.split(',') : null;
};

const SessionReport: React.FC<any> = ({ filter, load, setLoad, form }) => {
    const [reload, setReload] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<CheckboxValueType[]>(
        () => readCachedColumns() ?? defaultFields,
    );
    const [openPopover, setOpenPopover] = useState(false);
    const [displayColumns, setDisplayColumns] = useState<any>(() => readCachedColumns() ?? defaultFields);

    const intl = useIntl();

    useEffect(() => {
        if (filter !== null && load) {
            filterSession();
            setLoad(false);
        }
    }, [load]);

    const filterSession = () => {
        setReload(true);
    };

    const columns = [
        {
            title: intl.formatMessage({ id: 'general.date' }),
            dataIndex: 'date',
            key: 'date',
            sorter: true,
            render: (date: string) => moment(date).format(config.defaultDateFormat),
        },
        {
            title: intl.formatMessage({ id: 'general.start' }),
            dataIndex: 'start',
            key: 'start',
            sorter: true,
            render: (date: string) => date.substring(0, date.length - 3),
        },
        {
            title: intl.formatMessage({ id: 'general.end' }),
            dataIndex: 'end',
            key: 'end',
            sorter: true,
            render: (date: string) => date.substring(0, date.length - 3),
        },
        {
            title: intl.formatMessage({ id: 'general.session_length' }),
            dataIndex: 'sessionLength',
            key: 'sessionLength',
            sorter: true,
            render: (date: number) =>
                Math.floor(date / 60) + ':' + (date % 60 === 0 ? '00' : date % 60),
        },
        {
            title: intl.formatMessage({ id: 'general.status' }),
            dataIndex: 'status',
            key: 'status',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.session_title' }),
            dataIndex: 'sessionTitle',
            key: 'sessionTitle',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.fullname' }),
            dataIndex: 'fullName',
            key: 'fullName',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.position' }),
            dataIndex: 'position',
            key: 'position',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.organizationalUnit' }),
            dataIndex: 'organizationalUnit',
            key: 'organizationalUnit',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.department' }),
            dataIndex: 'department',
            key: 'department',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.organization' }),
            dataIndex: 'organization',
            key: 'organization',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.email' }),
            dataIndex: 'email',
            key: 'email',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'users.form.long_absence' }),
            dataIndex: 'absence',
            key: 'absence',
            render: (record: null | string) => <AbsenceTag status={!!record}/>
        },
    ];

    const content = (
        <div>
            <Checkbox.Group
                value={selectedColumns}
                onChange={(checkboxGroupColumns: CheckboxValueType[]) =>
                    setSelectedColumns(checkboxGroupColumns)
                }
            >
                {Object.values(columns).map((el: any) => {
                    return (
                        <div key={el.dataIndex} className={'flex p-1 gap-5'}>
                            <Checkbox key={el.dataIndex} value={el.dataIndex} />
                            {el.title}
                        </div>
                    );
                })}
            </Checkbox.Group>
            <Divider plain className={'m-2'} />
            <div className={'flex justify-between p-1'}>
                <Button type={'link'} onClick={() => handleCancel()} message='general.cancel' />
                <Button type={'primary'} onClick={() => handleColumnSubmit()} message='general.select' />
            </div>
        </div>
    );

    const handleColumnSubmit = () => {
        setDisplayColumns(selectedColumns);
        localStorage.setItem(cacheKey, selectedColumns.toString());
        setOpenPopover(false);
    };

    const handleCancel = () => {
        setSelectedColumns(readCachedColumns() ?? defaultFields);
        setOpenPopover(false);
    };

    const exportData = async () => {
        const values = form.getFieldsValue();
        const query = generalHelpers.filterParser(values).replace('&', '?');

        setExportLoading(true);
        try {
            const url = '/api/v1/courses/reports/export/LECTURE' + query;
            const response = await coursesApiClient.request(url, [], 'GET', true, true);

            if (response.fileName) {
                FileDownload(response, response.fileName);
            } else {
                message.error(intl.formatMessage({ id: 'error.data_load' }));
            }
        } catch (error) {
            message.error(intl.formatMessage({ id: 'error.data_load' }));
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className={'reports-element'}>
            <div className={'flex justify-between'}>
                <Button loading={exportLoading} type={'outline'} onClick={() => exportData()} message='general.export' />
                <Popover
                    open={openPopover}
                    placement="bottomRight"
                    title={intl.formatMessage({ id: 'general.columns' })}
                    content={content}
                    trigger="click"
                >
                    <Button type={'outline'} onClick={() => setOpenPopover(!openPopover)} icon='fa-solid fa-table-columns' message=' ' />
                </Popover>
            </div>
            <Table
                tableKey={'lecturesReport'}
                columns={columns.filter((el: any) => displayColumns.includes(el.dataIndex))}
                url={'/api/v1/courses/reports/session'}
                reload={reload}
                setReload={setReload}
                rowKey={'id'}
                filter={filter}
                scroll={{ x: 1900 }}
                fetchOnLoad={false}
                rowSelection={false}
                hasCustomFields={true}
                showTotalResults={true}
            />
        </div>
    );
};

export default SessionReport;
